<script lang="ts">
  import { t } from '$core/i18n';
  import type { PreferencesStore } from '$core/preferences';
  import { type SessionStore } from '$features/session';
  import { Button } from '$ui/primitives/Button';
  import { Badge } from '$ui/primitives/Badge';

  let { store, prefs }: { store: SessionStore; prefs: PreferencesStore } = $props();

  let frustration = $state(5);
  let energy = $state(5);
  let confirmingDiscard = $state(false);
  let saving = $state(false);

  // Cuando aparece el summary (pending_finish), cualquier recall en curso
  // se cancela sin persistir — como si el usuario nunca hubiera interactuado
  // con el ítem. Sin esto, un recall en curso se contaría parcialmente.
  $effect(() => {
    if (store.macro.state === 'pending_finish') {
      store.cancelActiveRecall();
    }
  });

  // Formato adaptativo: sub-minuto muestra segundos (evita "0 min" cuando
  // el usuario sí trabajó). [F9-4.1 microcopia, F6-34 lenguaje real]
  function formatDurationMin(ms: number): string {
    if (ms < 1000) return '0 s';
    if (ms < 60_000) return `${Math.floor(ms / 1000)} s`;
    const totalMin = Math.floor(ms / 60_000);
    const sec = Math.floor((ms % 60_000) / 1000);
    return sec > 0 ? `${totalMin} min ${sec} s` : `${totalMin} min`;
  }

  let focusDisplay = $derived(formatDurationMin(store.macro.focusAccumulatedMs));
  let readingDisplay = $derived(formatDurationMin(store.macro.readingAccumulatedMs));
  let avgMs = $derived(store.attemptsAverageMs);

  // Bloqueo de scroll del body mientras el modal está abierto (F8 §3.8).
  $effect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  });

  // ESC = KEEP STUDYING (seguro por defecto, no destructivo) — F6 §35.
  function handleEscape(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (confirmingDiscard) {
      cancelDiscard();
      return;
    }
    handleResume();
  }

  async function handleSave() {
    saving = true;
    try {
      await store.finalizeSession(frustration, energy);
    } finally {
      saving = false;
    }
  }

  function handleResume() {
    store.returnFromPending();
  }

  function askDiscard() {
    confirmingDiscard = true;
  }

  function cancelDiscard() {
    confirmingDiscard = false;
  }

  async function confirmDiscard() {
    await store.discardSession();
  }
</script>

<svelte:window onkeydown={handleEscape} />

<div
  class="overlay"
  role="dialog"
  aria-modal="true"
  aria-label={t('summary.title')}
  tabindex="-1"
>
  <div class="modal">
    <header>
      <div class="header-text">
        <h2>{t('summary.title')}</h2>
        <p class="subtitle">{t('summary.subtitle')}</p>
      </div>
      <button
        type="button"
        class="close-x"
        aria-label={t('summary.keepStudying')}
        onclick={handleResume}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3 3 L13 13 M13 3 L3 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </button>
    </header>

    <div class="grid">
      <div class="stat">
        <span class="stat-label">{t('summary.focus')}</span>
        <span class="stat-value">{focusDisplay}</span>
      </div>
      {#if prefs.prefs.showReadingTime}
      <div class="stat">
        <span class="stat-label">{t('summary.reading')}</span>
        <span class="stat-value">{readingDisplay}</span>
      </div>
      {/if}
      {#if prefs.prefs.showDistractions}
      <div class="stat">
        <span class="stat-label">{t('summary.distractions')}</span>
        <span class="stat-value">{store.macro.distractions}</span>
      </div>
      {/if}
    </div>

    <section class="attempts">
      <h3 class="section-label">{t('summary.attemptsTitle')}</h3>
      {#if store.attemptsCount === 0}
        <p class="empty">{t('summary.noAttempts')}</p>
      {:else}
        <div class="attempts-row">
          <Badge variant="success" shape="square" size="sm">
            ✓ {t('summary.correct')}: {store.attemptsCorrect}
          </Badge>
          <Badge variant="danger" shape="square" size="sm">
            ✕ {t('summary.incorrect')}: {store.attemptsIncorrect}
          </Badge>
          <Badge variant="warning" shape="square" size="sm">
            ⚠ {t('summary.unresolved')}: {store.attemptsUnresolved}
          </Badge>
        </div>
        {#if avgMs > 0}
          <div class="avg">
            <span class="stat-label">{t('summary.averageProblem')}</span>
            <span class="stat-value">{formatDurationMin(avgMs)}</span>
          </div>
        {/if}
      {/if}
    </section>

    {#if prefs.prefs.showFrustration || prefs.prefs.showEnergy}
    <section class="feelings fade-in">
      {#if prefs.prefs.showFrustration}
      <div class="slider-field">
        <div class="slider-head">
          <span>{t('summary.frustration')}</span>
          <span class="slider-value" aria-live="polite">{frustration}</span>
        </div>
        <input
          type="range"
          min="0"
          max="10"
          step="1"
          bind:value={frustration}
          aria-label={t('summary.frustration')}
          aria-valuetext={`${frustration} / 10`}
        />
        <div class="slider-scale" aria-hidden="true">
          <span>0</span><span>5</span><span>10</span>
        </div>
        <p class="slider-help">{t('summary.frustrationHelp')}</p>
      </div>
      {/if}
      {#if prefs.prefs.showEnergy}
      <div class="slider-field">
        <div class="slider-head">
          <span>{t('summary.energy')}</span>
          <span class="slider-value" aria-live="polite">{energy}</span>
        </div>
        <input
          type="range"
          min="0"
          max="10"
          step="1"
          bind:value={energy}
          aria-label={t('summary.energy')}
          aria-valuetext={`${energy} / 10`}
        />
        <div class="slider-scale" aria-hidden="true">
          <span>0</span><span>5</span><span>10</span>
        </div>
        <p class="slider-help">{t('summary.energyHelp')}</p>
      </div>
      {/if}
      <p class="hint">{t('summary.sliderHint')}</p>
    </section>
    {/if}

    {#if confirmingDiscard}
      <section class="confirm">
        <p class="warn">{t('summary.discardWarning')}</p>
        <div class="actions">
          <Button variant="danger" onclick={() => void confirmDiscard()}>
            {t('summary.confirmDiscard')}
          </Button>
          <Button variant="plain" onclick={cancelDiscard}>{t('summary.cancel')}</Button>
        </div>
      </section>
    {:else}
      <div class="actions">
        <Button variant="plain" onclick={askDiscard}>
          {t('summary.discard')}
        </Button>
        <span class="actions-spacer"></span>
        <Button variant="outline" onclick={handleResume}>
          {t('summary.keepStudying')}
        </Button>
        <Button
          variant="filled"
          loading={saving}
          disabled={saving}
          onclick={() => void handleSave()}
        >
          {saving ? t('summary.saving') : t('summary.save')}
        </Button>
      </div>
    {/if}
  </div>
</div>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: var(--color-scrim);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    z-index: var(--z-modal);
    animation: fade-in var(--dur-slow) var(--ease-out);
  }

  .modal {
    width: 100%;
    max-width: 38rem;
    max-height: calc(100dvh - var(--space-8));
    overflow-y: auto;
    background: var(--color-surface-elevated);
    border: var(--border-width) solid var(--color-border-strong);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    box-shadow: var(--shadow-md);
    animation: scale-in var(--dur-slow) var(--ease-out);
  }

  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-3);
  }

  .header-text {
    flex: 1;
    min-width: 0;
  }

  .close-x {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    color: var(--color-text-muted);
    cursor: pointer;
    transition: color var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out);
  }

  .close-x:hover {
    background: var(--color-surface-hover);
    color: var(--color-text-primary);
  }

  .close-x:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  header h2 {
    margin: 0 0 var(--space-1) 0;
    font-family: var(--font-display);
    font-size: 1.75rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.15;
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .subtitle {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    line-height: 1.5;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: var(--space-3);
  }

  @media (max-width: 480px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }

  .stat {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-3);
    background: var(--color-bg);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .stat-label,
  .section-label {
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    letter-spacing: var(--text-label-track);
    text-transform: uppercase;
    color: var(--color-text-muted);
    margin: 0;
  }

  .stat-value {
    font-family: var(--font-mono);
    font-size: var(--text-mono-data);
    font-weight: var(--weight-semibold);
    color: var(--color-text-primary);
    font-variant-numeric: tabular-nums;
  }

  .attempts {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .attempts-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .avg {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: var(--space-2) 0;
    border-top: var(--border-width) solid var(--color-border);
  }

  .empty {
    margin: 0;
    color: var(--color-text-muted);
    font-size: var(--text-sm);
  }

  .feelings {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .slider-field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .slider-head {
    display: flex;
    justify-content: space-between;
    font-size: var(--text-sm);
    color: var(--color-text-primary);
  }

  .slider-value {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-weight: var(--weight-semibold);
    color: var(--color-accent);
  }

  input[type='range'] {
    width: 100%;
    accent-color: var(--color-accent);
  }

  .slider-scale {
    display: flex;
    justify-content: space-between;
    font-size: var(--text-caption);
    font-family: var(--font-mono);
    color: var(--color-text-faint);
    padding: 0 2px;
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .confirm {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-3);
    border: var(--border-width) solid var(--color-danger);
    border-radius: var(--radius-md);
    background: var(--color-surface-sunken);
  }

  .warn {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-primary);
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    align-items: center;
  }

  .actions-spacer {
    flex: 1 1 auto;
  }

  @media (max-width: 640px) {
    .actions {
      flex-wrap: wrap;
    }
    .actions > :global(.btn) {
      flex: 1 1 auto;
    }
    .actions-spacer {
      display: none;
    }
  }

  .slider-help {
    margin: 0;
    font-size: var(--text-caption);
    color: var(--color-text-faint);
    line-height: 1.4;
  }

  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes scale-in {
    from { opacity: 0; transform: scale(0.98); }
    to { opacity: 1; transform: scale(1); }
  }
</style>